import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  increment,
  arrayUnion,
  runTransaction,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import {
  DEFAULT_READING_PROGRESS,
  DEFAULT_RESOURCE_PROGRESS,
  createEmptyParshaProgress,
  isParshaComplete,
  qualifiesForReadingStreak,
  type ReadingProgress,
  type ResourceReadingProgress,
  type ResourceProgress,
} from '@/types/reading';
import type { FileType } from '@/types/library';

// ============================================
// PATH HELPERS
// ============================================

function getReadingProgressPath(userId: string): string {
  return `artifacts/${appId}/users/${userId}/reading/progress`;
}

function getResourceProgressPath(userId: string): string {
  return `artifacts/${appId}/users/${userId}/reading/resources`;
}

// ============================================
// DATE UTILITIES
// ============================================

/**
 * Get today's date in YYYY-MM-DD format.
 */
function getTodayDateString(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Check if the date is today
 */
function isToday(dateStr: string): boolean {
  return dateStr === getTodayDateString();
}

/**
 * Check if the date is yesterday
 */
function isYesterday(dateStr: string): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return dateStr === yesterday.toISOString().split('T')[0];
}

// ============================================
// READING PROGRESS CORE FUNCTIONS
// ============================================

/**
 * Get or initialize user's reading progress.
 */
export async function getReadingProgress(userId: string): Promise<ReadingProgress> {
  const progressRef = doc(db, getReadingProgressPath(userId));
  const progressSnap = await getDoc(progressRef);

  if (!progressSnap.exists()) {
    const initialProgress: ReadingProgress = {
      ...DEFAULT_READING_PROGRESS,
    };

    await setDoc(progressRef, {
      ...initialProgress,
      updatedAt: serverTimestamp(),
    });

    return initialProgress;
  }

  return progressSnap.data() as ReadingProgress;
}

/**
 * Subscribe to reading progress changes (real-time).
 * Returns unsubscribe function.
 */
export function subscribeToReadingProgress(
  userId: string,
  callback: (progress: ReadingProgress | null) => void
): () => void {
  const progressRef = doc(db, getReadingProgressPath(userId));

  return onSnapshot(
    progressRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data() as ReadingProgress);
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error('Error subscribing to reading progress:', error);
      callback(null);
    }
  );
}

/**
 * Initialize or reset parsha progress for a new week.
 * Call when the weekly parsha changes.
 */
export async function initializeParshaProgress(
  userId: string,
  parshaRef: string,
  parshaTitle: string,
  parshaHebrew: string | undefined,
  totalVerses: number
): Promise<void> {
  const progressRef = doc(db, getReadingProgressPath(userId));
  const progress = await getReadingProgress(userId);

  // Check if we need to reset weekly stats (new parsha)
  const needsWeeklyReset = progress.weekResetParsha !== parshaTitle;

  const newParshaProgress = createEmptyParshaProgress(
    parshaRef,
    parshaTitle,
    parshaHebrew,
    totalVerses
  );

  const updates: Partial<ReadingProgress> = {
    currentParsha: newParshaProgress,
  };

  if (needsWeeklyReset) {
    updates.weeklyReadingTimeMs = 0;
    updates.weeklyVersesRead = 0;
    updates.weeklyResourcesViewed = 0;
    updates.weeklyResourcesCompleted = 0;
    updates.weekResetParsha = parshaTitle;
  }

  await updateDoc(progressRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Mark a verse as read.
 * Uses arrayUnion to add verse index atomically.
 */
export async function markVerseAsRead(
  userId: string,
  verseIndex: number
): Promise<{ isFirstVerse: boolean; newVersesCount: number }> {
  const progressRef = doc(db, getReadingProgressPath(userId));
  const progress = await getReadingProgress(userId);

  // Check if this is the first verse ever
  const isFirstVerse = progress.totalVersesRead === 0;

  // Check if verse was already read
  if (progress.currentParsha.versesRead.includes(verseIndex)) {
    return { isFirstVerse: false, newVersesCount: progress.currentParsha.versesRead.length };
  }

  await updateDoc(progressRef, {
    'currentParsha.versesRead': arrayUnion(verseIndex),
    'currentParsha.lastReadAt': serverTimestamp(),
    totalVersesRead: increment(1),
    weeklyVersesRead: increment(1),
    updatedAt: serverTimestamp(),
  });

  return {
    isFirstVerse,
    newVersesCount: progress.currentParsha.versesRead.length + 1,
  };
}

/**
 * Batch mark multiple verses as read.
 * More efficient than calling markVerseAsRead for each verse.
 */
export async function markVersesAsRead(
  userId: string,
  verseIndices: number[]
): Promise<{ newVersesMarked: number; isFirstVerse: boolean }> {
  const progressRef = doc(db, getReadingProgressPath(userId));
  const progress = await getReadingProgress(userId);

  // Filter out already-read verses
  const newVerses = verseIndices.filter((idx) => !progress.currentParsha.versesRead.includes(idx));

  if (newVerses.length === 0) {
    return { newVersesMarked: 0, isFirstVerse: false };
  }

  const isFirstVerse = progress.totalVersesRead === 0;

  await updateDoc(progressRef, {
    'currentParsha.versesRead': arrayUnion(...newVerses),
    'currentParsha.lastReadAt': serverTimestamp(),
    totalVersesRead: increment(newVerses.length),
    weeklyVersesRead: increment(newVerses.length),
    updatedAt: serverTimestamp(),
  });

  return { newVersesMarked: newVerses.length, isFirstVerse };
}

/**
 * Update reading session time.
 * Call periodically while reader is open.
 */
export async function updateReadingTime(userId: string, additionalTimeMs: number): Promise<void> {
  const progressRef = doc(db, getReadingProgressPath(userId));

  await updateDoc(progressRef, {
    'currentParsha.totalReadingTimeMs': increment(additionalTimeMs),
    totalReadingTimeMs: increment(additionalTimeMs),
    weeklyReadingTimeMs: increment(additionalTimeMs),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Increment commentary expansions count.
 */
export async function incrementCommentaryExpansions(userId: string): Promise<void> {
  const progressRef = doc(db, getReadingProgressPath(userId));

  await updateDoc(progressRef, {
    'currentParsha.commentariesExpanded': increment(1),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Increment highlights count.
 */
export async function incrementHighlights(userId: string): Promise<void> {
  const progressRef = doc(db, getReadingProgressPath(userId));

  await updateDoc(progressRef, {
    'currentParsha.highlightsAdded': increment(1),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Increment verse favorites count.
 */
export async function incrementVerseFavorites(userId: string): Promise<void> {
  const progressRef = doc(db, getReadingProgressPath(userId));

  await updateDoc(progressRef, {
    'currentParsha.versesFavorited': increment(1),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Mark current parsha as completed.
 * Call when 80%+ verses are read.
 * Uses a transaction to prevent concurrent calls from double-incrementing totalParshasCompleted.
 */
export async function markParshaCompleted(userId: string): Promise<boolean> {
  const progressRef = doc(db, getReadingProgressPath(userId));

  return runTransaction(db, async (transaction) => {
    const progressSnap = await transaction.get(progressRef);
    if (!progressSnap.exists()) return false;

    const progress = progressSnap.data() as ReadingProgress;

    // Check if already completed
    if (progress.currentParsha.completedAt) {
      return false;
    }

    // Verify completion threshold
    if (!isParshaComplete(progress.currentParsha)) {
      return false;
    }

    const isFirstParsha = progress.totalParshasCompleted === 0;

    transaction.update(progressRef, {
      'currentParsha.completedAt': serverTimestamp(),
      totalParshasCompleted: increment(1),
      updatedAt: serverTimestamp(),
    });

    return isFirstParsha;
  });
}

/**
 * Check and update reading streak.
 * Call after reading activity to maintain streak.
 * Uses a transaction to prevent concurrent sessions from reading the same streak value.
 */
export async function checkAndUpdateStreak(
  userId: string,
  sessionReadingTimeMs: number,
  sessionVersesRead: number
): Promise<{ streakUpdated: boolean; newStreak: number; isNewStreakDay: boolean }> {
  // Check if this activity qualifies for streak (pure computation, no DB needed)
  if (!qualifiesForReadingStreak(sessionReadingTimeMs, sessionVersesRead)) {
    const progress = await getReadingProgress(userId);
    return {
      streakUpdated: false,
      newStreak: progress.currentReadingStreak,
      isNewStreakDay: false,
    };
  }

  const progressRef = doc(db, getReadingProgressPath(userId));

  return runTransaction(db, async (transaction) => {
    const progressSnap = await transaction.get(progressRef);
    if (!progressSnap.exists()) {
      return { streakUpdated: false, newStreak: 0, isNewStreakDay: false };
    }

    const progress = progressSnap.data() as ReadingProgress;
    const today = getTodayDateString();
    const lastReadDate = progress.lastReadDate;

    // If already read today, no streak change
    if (lastReadDate === today) {
      return {
        streakUpdated: false,
        newStreak: progress.currentReadingStreak,
        isNewStreakDay: false,
      };
    }

    let newStreak: number;
    let longestStreak = progress.longestReadingStreak;

    if (isYesterday(lastReadDate)) {
      // Continue streak
      newStreak = progress.currentReadingStreak + 1;
    } else if (isToday(lastReadDate)) {
      // Same day, no change
      newStreak = progress.currentReadingStreak;
    } else {
      // Streak broken, start fresh
      newStreak = 1;
    }

    // Update longest streak if needed
    if (newStreak > longestStreak) {
      longestStreak = newStreak;
    }

    transaction.update(progressRef, {
      currentReadingStreak: newStreak,
      longestReadingStreak: longestStreak,
      lastReadDate: today,
      updatedAt: serverTimestamp(),
    });

    return {
      streakUpdated: true,
      newStreak,
      isNewStreakDay: lastReadDate !== today,
    };
  });
}

// ============================================
// RESOURCE PROGRESS FUNCTIONS
// ============================================

/**
 * Get or initialize user's resource reading progress.
 */
export async function getResourceProgress(userId: string): Promise<ResourceReadingProgress> {
  const progressRef = doc(db, getResourceProgressPath(userId));
  const progressSnap = await getDoc(progressRef);

  if (!progressSnap.exists()) {
    const initialProgress: ResourceReadingProgress = {
      ...DEFAULT_RESOURCE_PROGRESS,
    };

    await setDoc(progressRef, {
      ...initialProgress,
      updatedAt: serverTimestamp(),
    });

    return initialProgress;
  }

  return progressSnap.data() as ResourceReadingProgress;
}

/**
 * Subscribe to resource progress changes (real-time).
 * Returns unsubscribe function.
 */
export function subscribeToResourceProgress(
  userId: string,
  callback: (progress: ResourceReadingProgress | null) => void
): () => void {
  const progressRef = doc(db, getResourceProgressPath(userId));

  return onSnapshot(
    progressRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data() as ResourceReadingProgress);
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error('Error subscribing to resource progress:', error);
      callback(null);
    }
  );
}

/**
 * Update progress for a specific resource.
 * Uses a transaction to prevent concurrent views from overwriting each other's progress.
 */
export async function updateResourceViewProgress(
  userId: string,
  resourceId: string,
  fileId: string,
  title: string,
  type: FileType,
  updates: {
    additionalTimeMs?: number;
    scrollProgress?: number;
    mediaProgress?: number;
  }
): Promise<{ isNewView: boolean }> {
  if (!resourceId) {
    throw new Error('updateResourceViewProgress: resourceId must not be empty');
  }
  const progressRef = doc(db, getResourceProgressPath(userId));
  const readingProgressRef = doc(db, getReadingProgressPath(userId));

  return runTransaction(db, async (transaction) => {
    const progressSnap = await transaction.get(progressRef);

    let resourceProgress: ResourceReadingProgress;
    if (!progressSnap.exists()) {
      resourceProgress = { ...DEFAULT_RESOURCE_PROGRESS };
      transaction.set(progressRef, { ...resourceProgress, updatedAt: serverTimestamp() });
    } else {
      resourceProgress = progressSnap.data() as ResourceReadingProgress;
    }

    const existing = resourceProgress.resources[resourceId];
    const isNewView = !existing;

    // Prepare resource update
    const resourceUpdate: Partial<ResourceProgress> = {
      fileId,
      title,
      type,
      lastViewedAt: Timestamp.now(),
    };

    if (updates.additionalTimeMs !== undefined) {
      resourceUpdate.totalViewTimeMs = (existing?.totalViewTimeMs || 0) + updates.additionalTimeMs;
    }

    if (updates.scrollProgress !== undefined) {
      resourceUpdate.scrollProgress = Math.max(
        existing?.scrollProgress || 0,
        updates.scrollProgress
      );
    }

    if (updates.mediaProgress !== undefined) {
      resourceUpdate.mediaProgress = Math.max(existing?.mediaProgress || 0, updates.mediaProgress);
    }

    if (isNewView) {
      resourceUpdate.viewCount = 1;
      resourceUpdate.totalViewTimeMs = updates.additionalTimeMs || 0;
      resourceUpdate.scrollProgress = updates.scrollProgress || 0;
      resourceUpdate.mediaProgress = updates.mediaProgress || 0;
    } else {
      resourceUpdate.viewCount = (existing?.viewCount || 0) + 1;
    }

    transaction.update(progressRef, {
      [`resources.${resourceId}`]: {
        ...existing,
        ...resourceUpdate,
      },
      updatedAt: serverTimestamp(),
    });

    // Update weekly resources viewed if new view
    if (isNewView) {
      // Read the reading progress doc inside the transaction too
      const readingSnap = await transaction.get(readingProgressRef);
      if (readingSnap.exists()) {
        transaction.update(readingProgressRef, {
          weeklyResourcesViewed: increment(1),
          updatedAt: serverTimestamp(),
        });
      }
    }

    return { isNewView };
  });
}

/**
 * Mark a resource as completed.
 * Uses a transaction for multi-document atomicity - reads both resource and reading
 * progress, then writes to both. Prevents double-counting completions.
 */
export async function markResourceCompleted(
  userId: string,
  resourceId: string
): Promise<{ isFirstResource: boolean }> {
  if (!resourceId) {
    throw new Error('markResourceCompleted: resourceId must not be empty');
  }
  const progressRef = doc(db, getResourceProgressPath(userId));
  const readingProgressRef = doc(db, getReadingProgressPath(userId));

  return runTransaction(db, async (transaction) => {
    const [progressSnap, readingSnap] = await Promise.all([
      transaction.get(progressRef),
      transaction.get(readingProgressRef),
    ]);

    const resourceProgress = progressSnap.exists()
      ? (progressSnap.data() as ResourceReadingProgress)
      : { ...DEFAULT_RESOURCE_PROGRESS };
    const readingProgress = readingSnap.exists()
      ? (readingSnap.data() as ReadingProgress)
      : { ...DEFAULT_READING_PROGRESS };

    const existing = resourceProgress.resources[resourceId];

    // Already completed
    if (existing?.completedAt) {
      return { isFirstResource: false };
    }

    const isFirstResource = readingProgress.totalResourcesCompleted === 0;

    // Mark resource as completed
    transaction.update(progressRef, {
      [`resources.${resourceId}.completedAt`]: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Update totals
    transaction.update(readingProgressRef, {
      totalResourcesCompleted: increment(1),
      weeklyResourcesCompleted: increment(1),
      updatedAt: serverTimestamp(),
    });

    return { isFirstResource };
  });
}

// ============================================
// ADMIN / LEADERBOARD FUNCTIONS
// ============================================

/**
 * Get reading stats for a user (for admin/leaderboard display).
 */
export async function getUserReadingStats(userId: string): Promise<{
  totalParshasCompleted: number;
  totalVersesRead: number;
  totalReadingTimeMs: number;
  totalResourcesCompleted: number;
  currentReadingStreak: number;
  longestReadingStreak: number;
} | null> {
  try {
    const progress = await getReadingProgress(userId);
    return {
      totalParshasCompleted: progress.totalParshasCompleted,
      totalVersesRead: progress.totalVersesRead,
      totalReadingTimeMs: progress.totalReadingTimeMs,
      totalResourcesCompleted: progress.totalResourcesCompleted,
      currentReadingStreak: progress.currentReadingStreak,
      longestReadingStreak: progress.longestReadingStreak,
    };
  } catch (error) {
    console.error('Error getting user reading stats:', error);
    return null;
  }
}
